import { defineStore } from "pinia"
import { ref, type Ref } from "vue"
import { ProductionStatementApproval } from "@/models/production-statement-approval.model"

import { ModelFactory } from "@/factories/model-factory"
import ModelManager from "@/services/model-manager"

export const useProductionStatementApprovalStore = defineStore(
  "productionStatementApprovalStore",
  () => {
    const productionStatementApprovals: Ref<ProductionStatementApproval[]> = ref([])

    // Mounted cards register their autosave barrier. Keep it registered until an unmounted card's save finishes.
    const saveHandlers = new Map<string, Set<() => Promise<boolean>>>()

    function registerSaveHandler(id: string, save: () => Promise<boolean>) {
      const handlers = saveHandlers.get(id) ?? new Set<() => Promise<boolean>>()
      handlers.add(save)
      saveHandlers.set(id, handlers)
      return () => {
        handlers.delete(save)
        if (!handlers.size) saveHandlers.delete(id)
      }
    }

    async function flushSaves(ids: string[]) {
      const results = await Promise.all(
        ids.flatMap((id) => [...(saveHandlers.get(id) ?? [])].map((save) => save()))
      )
      if (results.some((saved) => !saved)) {
        throw new Error("Het klantakkoord kon niet worden opgeslagen. Probeer opnieuw.")
      }
    }

    const manualDraft: Ref<ProductionStatementApproval | null> = ref(null)

    function restoreManualDraft() {
      const draft = manualDraft.value
      if (
        draft &&
        !draft.isSaved &&
        !productionStatementApprovals.value.some((approval) => approval.id === draft.id)
      ) {
        productionStatementApprovals.value.unshift(draft)
      }
    }

    function openManualDraft(): ProductionStatementApproval {
      if (!manualDraft.value || manualDraft.value.isSaved) {
        manualDraft.value = ModelFactory.createModel(ProductionStatementApproval)
      }
      restoreManualDraft()
      return manualDraft.value
    }

    function discardEmptyDraft(approval: ProductionStatementApproval) {
      if (
        manualDraft.value?.id !== approval.id ||
        approval.isSaved ||
        approval.hasApprovalContent()
      )
        return
      productionStatementApprovals.value = productionStatementApprovals.value.filter(
        (item) => item.id !== approval.id
      )
      ModelManager.getInstance().evict(approval.id!)
      manualDraft.value = null
    }

    return {
      registerSaveHandler,
      flushSaves,
      productionStatementApprovals,
      manualDraft,
      openManualDraft,
      restoreManualDraft,
      discardEmptyDraft
    }
  }
)
